import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/lib/cart";

const NAV_LEFT = [
  { to: "/journal", label: "Journal" },
  { to: "/rituals", label: "Rituals" },
  { to: "/philosophy", label: "Philosophy" },
];
const NAV_RIGHT = [
  { to: "/library", label: "Library" },
  { to: "/quiz", label: "Ritual Finder" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const { count, setOpen: openCart } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const solid = !isHome || scrolled || open;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-bone border-b border-hairline" : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Left nav */}
          <nav className="hidden md:flex items-center gap-8 lg:gap-10 flex-1">
            {NAV_LEFT.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `text-[0.72rem] tracking-[0.22em] uppercase font-medium link-underline ${
                    isActive ? "text-ink" : "text-charcoal hover:text-ink"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <Link to="/" data-testid="logo-home" className="flex-shrink-0 mx-auto md:mx-0 select-none" aria-label="Still Form home">
            <span className="font-display text-[1.6rem] md:text-[1.9rem] tracking-[-0.02em] leading-none text-ink">
              still <span className="italic font-light">form</span>
            </span>
          </Link>

          {/* Right nav */}
          <nav className="hidden md:flex items-center gap-8 lg:gap-10 flex-1 justify-end">
            {NAV_RIGHT.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `text-[0.72rem] tracking-[0.22em] uppercase font-medium link-underline ${
                    isActive ? "text-ink" : "text-charcoal hover:text-ink"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Mobile right: menu toggle only (pre-launch — cart hidden) */}
          <div className="md:hidden absolute right-6 flex items-center gap-4">
            <button
              data-testid="mobile-menu-toggle"
              className="text-ink"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-hairline bg-bone" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-6 gap-4">
            {[...NAV_LEFT, ...NAV_RIGHT].map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="text-[0.78rem] tracking-[0.22em] uppercase font-medium py-2 border-b border-hairline-subtle text-ink"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
